<template>
  <footer id="footer" class="embfooter">
    <div
      @click="updateFooter"
      :class="['emb_collapsedicon dsize', { show: !open }]"
    >
      <StackIcon class="stack-icon" width="36px" height="36px" />
      <div aria-label="mobile legends" class="mobile-legends">
        <h3 class="mobile-legends-title">Legend</h3>
        <div class="embfooter__status">
          <div
            class="embfooter__status__list"
            v-for="item in footerData"
            :key="item.status"
            :style="{ color: item.color }"
          >
            <span
              class="dsize"
              :style="{ backgroundColor: item.color, color: item.color }"
            ></span>
          </div>
          <div
            class="embfooter__status__list"
            style="color: rgba(146, 159, 0, 1)"
            v-if="amenities?.length"
          >
            <img
              class="ammenity-icon"
              src="../assets/images/amenity-icon.svg"
              alt=""
            />
          </div>
        </div>
      </div>
    </div>

    <div :class="['embfooter-card', { 'fade-out': !open }]">
      <i
        aria-label="close icon"
        class="emb_closeicon is-gray"
        @click="updateFooter"
      ></i>
      <div class="emb-card_body">
        <div class="embfooter__ehologo">
          <img src="../assets/images/EHO-LOGO.svg" class="embfooter_ehosvg" />
        </div>
        <div class="embfooter__status">
          <div
            class="embfooter__status__list"
            v-for="item in footerData"
            :key="item.status"
            :style="{ color: item.color }"
          >
            <span
              class="dsize"
              :style="{ backgroundColor: item.color, color: item.color }"
            ></span>
            {{ item.status }}
          </div>
          <div
            class="embfooter__status__list"
            style="color: rgba(146, 159, 0, 1)"
            v-if="amenities?.length"
          >
            <img
              class="ammenity-icon"
              src="../assets/images/amenity-icon.svg"
              alt=""
            />
            Amenity
          </div>
        </div>
        <p class="embfooter__disclaimer">{{ messages.disclaimer }}</p>
      </div>
    </div>
  </footer>
</template>

<script lang="ts" setup>
import { ref, onMounted, nextTick, computed, toRefs, onUpdated } from "vue";
import StackIcon from "@/assets/icons/StackIcon.vue";
import { apiMessages } from "./../api/message-api";
import { Status, OrderFooter } from "@/constant";
import type {
  Amenity,
  CommunityQmiDetails,
  ComparedSvgFooterData,
  FooterData,
  LotCountNeighborhood,
} from "../types";

interface Props {
  open: boolean;
  selectedNeighborhoodIds: string[];
  data: FooterData[];
  amenities: Amenity[];
  lotsCount: LotCountNeighborhood[];
  futureHomePlans: CommunityQmiDetails;
  matchedSvgfooterData: ComparedSvgFooterData[];
}

const props = defineProps<Props>();
const {
  open,
  selectedNeighborhoodIds,
  data,
  amenities,
  lotsCount,
  futureHomePlans,
  matchedSvgfooterData,
} = toRefs(props);

const emit = defineEmits([
  "toggleFooter",
  "setLegends",
  "recalculateSidebarHeight",
]);

const messages = ref<any>({});
const height = ref(0);

const loadMessages = async () => {
  const { messages: apiMsg } = await apiMessages("AppFooter");
  messages.value = apiMsg;
};

const updateFooter = () => emit("toggleFooter");

const changeFooterHeight = () => {
  const legendDiv = document.getElementsByClassName("embfooter-card")[0];
  if (!legendDiv) return;

  const resizeObserver = new ResizeObserver(() => {
    height.value = (legendDiv as any).offsetHeight;
    document.documentElement.style.setProperty(
      "--footerLegendHeight",
      `${height.value}px`
    );
  });
  resizeObserver.observe(legendDiv);
};

const isPresentLotWithStatus = (status: string) => {
  const lotType = status.toLowerCase().replace(" ", "").replace("-", "");
  if (selectedNeighborhoodIds.value.length) {
    return lotsCount.value.find(
      (neighborhoodLot) =>
        neighborhoodLot.neighborhoodId === selectedNeighborhoodIds.value[0]
    )?.[lotType];
  } else {
    return lotsCount.value.find(
      (neighborhoodLot) => neighborhoodLot?.[lotType]
    )?.[lotType];
  }
};

const sortFooter = (updateFooterData: FooterData[]) => {
  const sortedFooterData: FooterData[] = [];
  for (const order of OrderFooter) {
    const found = updateFooterData.find((el) => el.status === order);
    if (found) sortedFooterData.push(found);
  }
  return sortedFooterData;
};
const updateFooterAsSvgData = (footerData: FooterData[]) => {
  footerData.forEach((data, index) => {
    const matchedData = matchedSvgfooterData.value.filter((d) =>
      d.status.includes(data.status.toLowerCase())
    );
    const updateNeighborhoods = matchedData.map((d) => d.neighborhood_code);
    if (!updateNeighborhoods.length) {
      footerData.splice(index, 1);
    } else {
      data.neighborhoods = updateNeighborhoods;
    }
  });
};

const footerFutureHomePlan = (footerData: FooterData[]) => {
  if (
    (!selectedNeighborhoodIds.value.length &&
      Object.keys(futureHomePlans.value).length) ||
    Object.keys(futureHomePlans.value).includes(
      selectedNeighborhoodIds.value[0]
    )
  ) {
    footerData.push({
      color: "#CBBE98",
      neighborhoods: [...Object.keys(futureHomePlans.value)],
      status: Status.FUTURE_HOME,
    });
  }
  updateFooterAsSvgData(footerData);
};

const footerData = computed(() => {
  let futureHomeFooterData =
    data.value.filter((item) => isPresentLotWithStatus(item.status)) || [];
  footerFutureHomePlan(futureHomeFooterData);
  futureHomeFooterData = sortFooter(futureHomeFooterData);

  if (selectedNeighborhoodIds.value.length) {
    futureHomeFooterData = futureHomeFooterData.filter((d) =>
      d.neighborhoods.includes(selectedNeighborhoodIds.value[0])
    );
  }

  const legendArr = futureHomeFooterData.map((plan) => plan.status).toString();
  emit("setLegends", legendArr);
  return futureHomeFooterData;
});

onMounted(() => {
  loadMessages();
  nextTick(() => changeFooterHeight());
});

const recalculateSidebarHeight = () => emit("recalculateSidebarHeight");

onUpdated(() => recalculateSidebarHeight());
</script>

<style lang="scss" scoped>
.embfooter {
  position: absolute;
  bottom: 0;
  right: 0;
  left: 0;
  transition: all 0.6s linear;
  z-index: map-get($z-index, embsidebar);

  @include md {
    z-index: map-get($z-index, embfooter-close);
    bottom: var(--bottomSpacelegend);
    left: var(--leftSidebarWidth);
    z-index: map-get($z-index, embfooter-close);
  }

  .emb_closeicon {
    --dsize: 24px;
    --cardPaddingX: 21px;
    --cardPaddingY: 12px;
    right: 10px;
    top: 12px;

    @include md {
      --dsize: 20px;
      right: 20px;
      top: 30px;
    }

    @include lg {
      top: 20px;
    }

    @include xl {
      top: 12px;
    }
  }

  .stack-icon {
    display: none;

    @include md {
      display: block;
    }
  }

  .mobile-legends {
    display: grid;
    padding: 5px 11px 10px;
    box-shadow: 0px 2px 3px rgba(163, 131, 63, 0.13);
    cursor: pointer;

    @include md {
      display: none;
    }

    &-title {
      color: $black;
      @include font(10px, 600);
    }

    .embfooter__status {
      padding: 0;
      border: none;
      gap: 8px;
      margin-bottom: 0;
    }
  }

  .emb_collapsedicon {
    --dsize: auto;
    position: fixed;
    opacity: 0;
    visibility: hidden;
    z-index: map-get($z-index, embfooter-collapsedicon);
    cursor: pointer;

    @include mobile-standard {
      top: 174px;
      left: 20px;
      width: auto;
      background: rgba(255, 255, 255, 0.63);
      border-radius: 5px;
    }

    @include md {
      --dsizeBg: #a8b50a;
      --dsize: 57px;
      position: absolute;
      right: 130px;
      bottom: 45px;
    }

    &.show {
      opacity: 1;
      visibility: visible;
    }
  }

  .embfooter-card {
    max-width: var(--footerLegendWidth);
    background: $white;
    margin-inline: auto;
    position: relative;
    overflow: hidden;
    border-radius: 4px 4px 0px 0px;
    box-shadow: 0px 2px 3px 0px rgba(163, 131, 63, 0.13);
    padding: 0 16px 23px;

    @include md {
      border-radius: 100vmax;
      padding: 13px 100px 13px 35px;
    }
  }

  .emb-card_body {
    display: grid;
    grid-template-columns: 35px 1fr;
    column-gap: 23px;

    @include md {
      column-gap: 15px;
    }
  }

  &__status {
    --dsize: 14px;
    order: 1;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 12px;
    row-gap: 10px;

    @include mobile-standard {
      border-bottom: 1px solid #d9d9d9;
      grid-column: 1 / 3;
      margin-bottom: 7px;
      padding-top: 47px;
      padding-bottom: 35px;
    }

    @media (min-width: 414px) {
      column-gap: 28px;
      justify-content: center;
      padding-inline: 26px;
    }

    @include md {
      --dsize: 16px;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      order: 2;
      column-gap: 10px;
      padding: 0;
      row-gap: 5px;
      margin-bottom: 5px;
      margin-left: -50px;
    }

    @include xl {
      column-gap: 12px;
      margin-left: 0;
    }

    &__list {
      @include font(12px, 600);
      display: flex;
      align-items: center;
      gap: 10px;
      white-space: nowrap;

      @include md {
        line-height: 29px;
        font-size: 14px;

        &:not(:last-child) {
          border-right: 1px solid #d9d9d9;
          padding-right: 9px;
        }
      }
    }
  }

  &__disclaimer {
    width: 100%;
    @include font(10px, 400);
    color: $gray-600;
    line-height: 15px;
    margin-bottom: 0;
    order: 3;

    @include md {
      text-align: center;
    }
  }

  &__ehologo {
    order: 2;

    @include md {
      align-self: center;
      grid-row-end: span 2;
      order: 1;
    }
  }

  &__ehosvg {
    width: 100%;
    height: 100%;
    max-width: 35px;
  }

  .ammenity-icon {
    width: 11px;
    height: 17px;
  }
}
</style>
